// ============================================================
// GateSheet.js
// A complete chain link gate shop drawing sheet — sheet outline, title block,
// data column, and the frame cut sheet itself.
//
// Modelled on the shop's hand-drawn sheet: a bordered page with a right-hand
// data column and a bottom strip carrying the company, the customer, and the
// scale note.
//
// The frame drawing is NOT duplicated here — GateElevation is nested as a
// child <svg>, which carries its own 600x400 viewBox and so scales itself into
// whatever box this sheet gives it.
//
// Pure black line art, Manrope, no fills — reproduces on any copier.
// ============================================================

import React from 'react';
import GateElevation from './GateElevation';
import PlateDetail, {
  postOdLabel, plateSizeLabel, thicknessLabel, postLengthText, normalizePlatePosition, MAX_PLATES_PER_SHEET,
} from './PlateDetail';
import { computeGateCuts, formatFeetInches } from '../utils/gateDrawing';

// A POST PLATES drawing puts PlateDetail in the drawing area instead of
// GateElevation, and swaps the gate-specific half of the data column. The sheet
// itself — border, data column, bottom strip, headings, signature block — is
// the same one, which is the point: this is a new DRAWING, not a new sheet.
// Gates and plates never share a sheet.
export const PLATE_TYPE = 'Post Plates';
const isPlateDrawing = (gate) => gate?.type === PLATE_TYPE;

// Sheet geometry, in viewBox units.
const SHEET_W = 850;
const SHEET_H = 620;
const INSET = 10;                     // border inset from the sheet edge
// Right data column divider. Moved out from 620 to widen the drawing. The
// blocker was the bold bracing warning, which is now split across two lines,
// so the column narrows to 690. The longest remaining string is
// "3 Strand Barbed Wire", which clears the sheet border with room to spare.
const COL_X = 690;
// Bottom strip rule. Raised from 530 to 515 to make room for a fourth line in
// the left cell ("Drawn by"); the drawing area above still clears it easily,
// so no rescaling was needed.
const STRIP_Y = 515;
const STRIP_SPLIT_X = 380;            // company | customer split in the strip

// Bottom strip text baselines — four evenly spaced lines.
const STRIP_LINE_1 = 541;
const STRIP_LINE_2 = 561;
const STRIP_LINE_3 = 581;
const STRIP_LINE_4 = 601;

const INK = '#000000';
const W_RULE = 1;

// Data column text.
const COL_TEXT_X = 704;               // left-aligned content
const DIM_HEIGHT_X = 776;             // second dimension number's left edge

// Drawing area: left of the data column, above the bottom strip.
const DRAW_X = 25;
const DRAW_Y = 75;
// Widened by the divider move, keeping a 25-unit gutter to the column.
// DRAW_H is derived from DRAW_W at the GateElevation viewBox's 3:2 ratio, so
// the drawing can never be stretched by a width change.
const DRAW_W = 640;
const DRAW_H = (DRAW_W * 2) / 3;
// The blank sheet's width is pinned to this gutter rather than derived from
// DRAW_W, so moving the divider or resizing the normal drawing can never
// change the blank template.
const BLANK_GUTTER = 25;

// Tube OD in inches → the label the shop writes on the sheet. Covers the swing
// frame sizes and the cantilever's fixed rail and upright sizes.
const FRAME_OD_LABEL = {
  1.375: '1-3/8"',
  1.625: '1-5/8"',
  2: '2"',
  2.5: '2-1/2"',
};
const odLabel = (od) => FRAME_OD_LABEL[od] || (od ? od + '"' : '');

// Signature block — customer copy only. The rules sit clear of the orientation
// note (y=505) and the bottom strip (y=515); the drawing gives up the room.
const SIG_RULE_Y = 470;
const SIG_LABEL_Y = 484;
const SIG_CLEARANCE = 16;             // drawing bottom → signature rule
const SIG_GAP = 40;                   // between the two rules
const SIG_NAME_SHARE = 0.6;           // signature is wider than the date

// Sheet title block, above the drawing.
const HEADING_Y = 45;                 // CUSTOMER APPROVAL COPY

// Opening-size warning, customer copy only — a SECOND HEADING LINE directly
// under CUSTOMER APPROVAL COPY, centred the same way and set in the same bold
// caps. 16 units below it: the line step used everywhere else on this sheet
// (122/138, 178/194, 250/266).
//
// It reads as part of the title rather than as a footnote, which is the point —
// the customer should meet it before looking at the gate, not after.
const WARNING_Y = HEADING_Y + 16;     // 61
// Clearance from the warning's baseline down to the top of the drawing. The
// drawing gives up this room from its TOP; its floor is unchanged, because
// nothing sits between it and the signature rules any more.
const WARNING_GAP = 18;

// Quantity, top-left of the drawing area, left-anchored while the heading is
// centred. Its baseline sits just above the heading's so the 20pt block clears
// the warning line beneath — on a narrow sheet the centred warning can reach
// left toward the QTY column, and separating them vertically means the two can
// never touch whatever the sheet width works out to be.
const QTY_Y = 46;

// The two centred headings, as constants: the label above measures itself
// against them, and a heading whose text lived only in the JSX could be
// reworded without the measurement following it.
const HEADING_TEXT = 'CUSTOMER APPROVAL COPY';
const WARNING_TEXT = 'GATE BUILT TO OPENING SIZE, NOT ACTUAL SIZE';

// Overall height, top-RIGHT of the drawing area — the mirror of QTY, on the
// same baseline, right-anchored where QTY is left-anchored.
//
// It replaced two stacked vertical dimensions down the side of the cantilever.
// Those stated a true and necessary thing — a barbed gate finishes a foot
// taller than the height that was typed — but they stated it as MEASUREMENTS,
// on a sheet whose whole point is that it carries exactly one. A caption in the
// corner says the same thing without pretending to be a third dimension.
const HEIGHT_Y = QTY_Y;
// Where it drops to when a centred heading leaves no room beside it. 16 units
// below, the line step used everywhere else on this sheet (45/61, 122/138,
// 178/194, 250/266). Still the top-right corner, one line down.
const HEIGHT_SECOND_Y = QTY_Y + 16;
// Clearance between this label and whatever centred heading shares its line.
const HEIGHT_GAP = 12;
// Advance width per character as a fraction of the font size, for Manrope bold.
// Only ever used to decide whether the label FITS beside a heading, so it needs
// to be close, not exact — but it does need to distinguish the two classes.
//
// A flat average was tried and is too blunt: "12' + 1' = 13'" is more than half
// punctuation and spaces, a flat estimate reads it as a third wider than it is,
// and an ordinary 12 ft gate got pushed onto the second line for no reason. So
// digits and letters get one figure and the thin characters another.
const CHAR_W = 0.62;
const CHAR_W_NARROW = 0.34;
const NARROW_CHARS = " '\"+=-.";
const HEADING_CHAR_W = 0.66;
const textW = (text, size, spacing) => [...String(text || '')].reduce(
  (w, ch) => w + size * (NARROW_CHARS.includes(ch) ? CHAR_W_NARROW : CHAR_W) + spacing, 0);

/**
 * `blankSheet` produces a printable template for a one-off custom gate: the
 * frame is drawn to the form's settings (leaf count, barbed wire, mid rails),
 * but every NUMBER is left off so the shop can write measurements by hand.
 * The data column goes, the dimensions inside the drawing go, and the drawing
 * takes the full sheet width. The bottom strip is untouched — a template still
 * needs to say who drew it, when, and for whom.
 *
 * `gate.customerCopy` produces the sheet the CUSTOMER signs. It keeps every
 * dimension — the finished gate is exactly what they are approving — and drops
 * only the shop-only detail: the frame tube sizes here, and the end-upright cut
 * callout inside GateElevation. A signature block takes the bottom of the
 * drawing area.
 *
 * The two modes are independent. With both on, blank wins for the data column
 * (there is nothing left to hide) and the signature block still appears.
 *
 * `gate.quantity` above 1 prints a QTY callout at the top of the drawing on
 * EVERY version of the sheet — shop, customer and blank alike. The welder has
 * to know he is building three and the customer has to know he is getting
 * three. At a quantity of 1 nothing is drawn: the ordinary case earns no ink.
 */
export default function GateSheet({ gate, meta, blankSheet = false }) {
  // POST PLATES. computeGateCuts still runs and is still harmless — every gate
  // figure below reads 0/false for a type it does not recognise — but nothing
  // gate-shaped is DRAWN or printed for a plate drawing.
  const plateDrawing = isPlateDrawing(gate);
  const plates = plateDrawing ? (Array.isArray(gate?.plates) ? gate.plates : []) : [];
  const platesShown = plates.slice(0, MAX_PLATES_PER_SHEET);
  const plateTotal = platesShown.reduce((n, p) => n + Math.max(1, Math.round(parseFloat(p?.quantity) || 1)), 0);

  const cuts = computeGateCuts(gate);
  const frameLabel = odLabel(cuts.frameOdIn);
  const customerCopy = !!cuts.customerCopy;
  const frameOnly = !!cuts.frameOnly;
  const quantity = cuts.quantity || 1;
  // The gate QTY callout counts whole gates. A plate drawing counts each plate
  // separately, in its own label, so the sheet-wide figure would be a second
  // quantity meaning something else.
  const showQty = !plateDrawing && quantity > 1;
  // "GATE BUILT TO OPENING SIZE" is a statement about a gate fitting an
  // opening. A plate has no opening, so it is never true here.
  const showOpeningWarning = !plateDrawing && !!cuts.showOpeningWarning;
  const m = meta || {};

  // ── THE TWO SUB-LABELS UNDER THE SIZE ────────────────────────────────
  //
  // WIDTH. Under actual size this number used to be sub-labelled "(rail cut)",
  // which was wrong and dangerous: it is the gate's OUTSIDE measurement — the
  // drawing dimensions it "48" OTO" — while the rail cut piece beside it is the
  // pipe the rail is actually cut from. Two different numbers reading as the
  // same thing on a cut sheet is how a rail gets cut wrong.
  //
  // The shop's version echoes the drawing's own dimension, so the sheet says
  // one thing twice rather than two similar things.
  //
  // The CUSTOMER's version spells it out. "OTO" is shop jargon, and their copy
  // deliberately drops the OTO dimension from the drawing (see GateElevation),
  // so on their sheet the abbreviation would have nothing to anchor to.
  // "(gate width)" also answers the question a customer actually has — is this
  // the gate or the gap? — and under actual size the gate really is built to
  // that figure, so it is true as well as plain.
  const widthSubLabel = !cuts.actualSize
    ? '(opening)'
    : (customerCopy ? '(gate width)' : '(width, OTO)');

  // HEIGHT. "(upright cut)" is CORRECT under actual size and is kept: the
  // entered height is taken verbatim as the upright cut, with no short-gate
  // deduction — which is the whole point of the mode.
  //
  // ONE EXCEPTION, and it is the same class of error as the width label above.
  // Barbed wire adds a flat 12" to the upright, so the upright is cut 12"
  // LONGER than this number: an 84" cut sitting under a label that says 72" is
  // the upright cut. There the number is the fence height, so that is what it
  // is called — the drawing carries the 6' + 1' split that explains the rest.
  const heightSubLabel = (cuts.actualSize && !cuts.barbedWire)
    ? '(upright cut)'
    : '(height)';

  // ── OVERALL HEIGHT, AS A BREAKDOWN ───────────────────────────────────
  // "6' + 1' = 7'". Written out rather than reduced to the total, because the
  // relationship IS the message: the shop and the customer both know the 6'
  // figure, and the label's job is to show what the barbed wire adds to it.
  //
  // CANTILEVER WITH BARBED WIRE ONLY. Without barbed wire the entered height is
  // the overall height, and a label saying 6' = 6' is noise. Swing gates carry
  // the same split as an actual DIMENSION down the side of the leaf, where
  // there is room for it, so they need nothing here.
  //
  // formatFeetInches gives 7'-0"; the drawing's own height labels drop an exact
  // zero and read 7'. That trimming lives in a private helper in gateDrawing.js
  // which is not exported, and reaching for it would mean editing a third file,
  // so the same rule is applied here to the exported formatter's output. The
  // verification asserts the two agree, which is what stops them drifting.
  const feetLabel = (inches) => formatFeetInches(inches).replace(/-0"$/, '');
  const showHeightSum = !!cuts.isCantilever && !!cuts.barbedWire;
  const fenceIn = Math.max(0, parseFloat(gate?.heightIn) || 0);
  const extraIn = cuts.barbExtensionIn || 0;
  const heightSumText = showHeightSum
    ? `${feetLabel(fenceIn)} + ${feetLabel(extraIn)} = ${feetLabel(fenceIn + extraIn)}`
    : '';

  // With the data column gone the drawing spans the whole sheet, keeping the
  // same left margin and the same clearance to the right border.
  const drawX = DRAW_X;
  const drawW = blankSheet ? (SHEET_W - INSET - DRAW_X - BLANK_GUTTER) : DRAW_W;
  const drawH = blankSheet ? drawW * (DRAW_H / DRAW_W) : DRAW_H;
  // The warning is a second title line, so it pushes the drawing's TOP down
  // rather than lifting its floor. Sheets without it start at DRAW_Y exactly as
  // they always have.
  const drawTop = showOpeningWarning ? WARNING_Y + WARNING_GAP : DRAW_Y;
  // Keep the taller full-width drawing inside the strip.
  const blankScale = blankSheet ? Math.min(1, (STRIP_Y - drawTop - 20) / drawH) : 1;
  // A customer copy hands the bottom of the drawing area to the signature
  // block, so the drawing is capped to whatever is left above the rules. Width
  // and height take the SAME factor, so the nested viewBox is scaled, never
  // stretched. Off a customer copy this is exactly 1 and the geometry is
  // bit-for-bit what it was.
  // The floor is the same on every customer copy now — nothing sits between the
  // drawing and the signature rules. Only the top varies.
  const copyRoom = SIG_RULE_Y - SIG_CLEARANCE - drawTop;
  const copyScale = customerCopy ? Math.min(1, copyRoom / (drawH * blankScale)) : 1;
  const drawScale = blankScale * copyScale;
  const finalW = drawW * drawScale;
  const finalH = drawH * drawScale;
  // Centred in its own area once shrunk, rather than hanging off to the left.
  // Off a customer copy finalW is DRAW_W and this collapses back to drawX.
  const finalX = blankSheet
    ? (SHEET_W - finalW) / 2
    : drawX + (DRAW_W - finalW) / 2;

  // ── KEEPING THE TOP LINE CLEAR ───────────────────────────────────────
  // Three things can share this baseline: QTY on the left, this label on the
  // right, and a centred heading between them. QTY is short and fixed-ish; the
  // heading is centred, so it eats outward from the middle in BOTH directions;
  // this label grows leftward from the right edge. The one that can run into
  // the heading is therefore this one.
  //
  // So the room is worked out rather than assumed: half the drawing, less half
  // of the widest heading actually being drawn, less a gap.
  //
  // WHEN IT DOES NOT FIT, IT MOVES DOWN A LINE — it does not shrink. Shrinking
  // was tried and is worse twice over: a 12pt label no longer matches QTY, and
  // the longest realistic label still ran into the heading even at 12pt, so it
  // bought illegibility without buying clearance. Dropping to the next line
  // keeps the size, keeps the corner, and is unambiguous.
  //
  // This only ever happens on a CUSTOMER COPY of a gate with a fractional
  // height — a 6'-6.5" fence — because that is the only combination where a
  // centred heading and a long label are on the sheet at once. Every whole-foot
  // height on every shop sheet stays on the QTY line at 20pt.
  //
  // Note the warning line cannot currently appear beside this label at all:
  // showOpeningWarning is false on a cantilever by construction. It is included
  // anyway, because a rule that only holds while a second rule holds elsewhere
  // is a rule that breaks the day somebody changes the second one.
  const headingHalfW = Math.max(
    customerCopy ? (HEADING_TEXT.length * (12 * HEADING_CHAR_W + 2)) / 2 : 0,
    showOpeningWarning ? (WARNING_TEXT.length * (11 * HEADING_CHAR_W + 0.5)) / 2 : 0,
  );
  const heightRoom = Math.max(0, finalW / 2 - headingHalfW - HEIGHT_GAP);
  const heightNaturalW = textW(heightSumText, 20, 1);
  const heightY = (headingHalfW > 0 && heightNaturalW > heightRoom)
    ? HEIGHT_SECOND_Y
    : HEIGHT_Y;

  // Signature rules span the drawing's own width, so the block reads as part of
  // it in either mode and can never cross the data column divider.
  const sigNameW = (finalW - SIG_GAP) * SIG_NAME_SHARE;
  const sigDateX = finalX + sigNameW + SIG_GAP;
  const sigDateW = finalX + finalW - sigDateX;

  return (
    <svg
      width="100%"
      viewBox={`0 0 ${SHEET_W} ${SHEET_H}`}
      role="img"
      aria-label={plateDrawing
        ? `Shop drawing ${m.drawingNumber || ''} — post plates, ${platesShown.length} size${platesShown.length === 1 ? '' : 's'}, ${plateTotal} plate${plateTotal === 1 ? '' : 's'} in total`
        : `Shop drawing ${m.drawingNumber || ''} — ${gate?.type || 'gate'} ${cuts.openingLabel} by ${cuts.heightLabel}`}
      style={{ display: 'block', fontFamily: 'Manrope, sans-serif' }}
    >
      {/* ── Sheet outline ── */}
      <rect
        x={INSET}
        y={INSET}
        width={SHEET_W - INSET * 2}
        height={SHEET_H - INSET * 2}
        fill="none"
        stroke={INK}
        strokeWidth={W_RULE}
      />

      {/* Right data column divider. On a blank sheet the drawing spans the full
          width, so the divider only runs through the BOTTOM STRIP — the strip
          keeps its three cells, but nothing crosses the drawing area. */}
      <line
        x1={COL_X}
        y1={blankSheet ? STRIP_Y : INSET}
        x2={COL_X}
        y2={SHEET_H - INSET}
        stroke={INK}
        strokeWidth={W_RULE}
      />

      {/* Bottom strip, and its company | customer split */}
      <line x1={INSET} y1={STRIP_Y} x2={SHEET_W - INSET} y2={STRIP_Y} stroke={INK} strokeWidth={W_RULE} />
      <line x1={STRIP_SPLIT_X} y1={STRIP_Y} x2={STRIP_SPLIT_X} y2={SHEET_H - INSET} stroke={INK} strokeWidth={W_RULE} />

      {/* ── Drawing area — the frame cut sheet, nested with its own viewBox ──
          On a blank sheet it spans the full width and drops its dimensions;
          the frame members, mesh and barbed wire are unaffected. */}
      {plateDrawing ? (
        <PlateDetail
          plates={platesShown}
          x={finalX}
          y={drawTop}
          width={finalW}
          height={finalH}
          hideDimensions={blankSheet}
        />
      ) : (
        <GateElevation
          gate={gate}
          x={finalX}
          y={drawTop}
          width={finalW}
          height={finalH}
          hideDimensions={blankSheet}
        />
      )}

      {/* ── Right data column ──
          Entirely omitted on a blank sheet: number, size, frame, finishes,
          gauge, barbed wire line and the diagonal warning are all measurements
          or identity the shop is going to fill in by hand. */}
      {/* ── Data column, POST PLATES ──
          The gate column below is wrong here line by line: a plate has no
          opening, no height, no frame tube, no mesh and no gauge. It gets the
          drawing number, what the sheet is, and a schedule of the plates — the
          same information a welder would otherwise count off the drawing.
          Dropped entirely on a blank sheet, exactly as the gate column is. */}
      {plateDrawing && !blankSheet && (
      <>
      <text x={COL_TEXT_X} y={40} fill={INK} fontSize="13" fontWeight="500">
        {m.drawingNumber || ''}
      </text>
      <text x={COL_TEXT_X} y={80} fill={INK} fontSize="13" fontWeight="700">POST PLATES</text>
      <text x={COL_TEXT_X} y={98} fill={INK} fontSize="9">
        {platesShown.length} size{platesShown.length === 1 ? '' : 's'} · {plateTotal} plate{plateTotal === 1 ? '' : 's'} total
      </text>
      {m.frameFinish && (
        <text x={COL_TEXT_X} y={122} fill={INK} fontSize="11">{m.frameFinish}</text>
      )}
      {/* The schedule: one row per plate, in the order they are drawn. */}
      {platesShown.map((p, i) => (
        <React.Fragment key={p?.id ?? i}>
          <text x={COL_TEXT_X} y={154 + i * 34} fill={INK} fontSize="11" fontWeight="700">
            {plateSizeLabel(p?.plateSizeIn)} × {Math.max(1, Math.round(parseFloat(p?.quantity) || 1))}
          </text>
          <text x={COL_TEXT_X} y={168 + i * 34} fill={INK} fontSize="9">
            {postOdLabel(p?.postOdIn)}{postLengthText(p) ? ` × ${postLengthText(p)}` : ''} post
            {' · '}{thicknessLabel(p?.thicknessIn)}
            {normalizePlatePosition(p?.position) === 'Offset' ? ' · offset' : ''}
          </text>
        </React.Fragment>
      ))}
      </>
      )}

      {!plateDrawing && !blankSheet && (
      <>
      {/* 1. Drawing number */}
      <text x={COL_TEXT_X} y={40} fill={INK} fontSize="13" fontWeight="500">
        {m.drawingNumber || ''}
      </text>

      {/* 2. Opening x height, with the smaller labels sitting under each number.
             Both numbers and both sub-labels are left-anchored at fixed columns,
             so they stay aligned whatever the digit count. */}
      <text x={COL_TEXT_X} y={80} fill={INK} fontSize="13">{cuts.openingLabel}</text>
      <text x={760} y={80} fill={INK} fontSize="13">x</text>
      <text x={DIM_HEIGHT_X} y={80} fill={INK} fontSize="13">{cuts.heightLabel}</text>
      {/* Under actual size these numbers are not an opening and a height, so
          the sub-labels say what they are instead. See widthSubLabel and
          heightSubLabel for why each reads the way it does. */}
      <text x={COL_TEXT_X} y={94} fill={INK} fontSize="9">
        {widthSubLabel}
      </text>
      <text x={DIM_HEIGHT_X} y={94} fill={INK} fontSize="9">
        {heightSubLabel}
      </text>

      {/* 3-6. Frame and mesh specification.
             A cantilever frame is fixed BY THE PRODUCT, not by grade — 2-1/2"
             rails carrying 2" uprights — so the grade-derived single Frame
             line is simply wrong there, and two real sizes take its place.
             Both come from the engine, never from a literal written here.
             Dropped entirely on a customer copy: tube sizes are shop detail. */}
      {/* SHOWN ON THE CUSTOMER COPY TOO. These lines were hidden as shop
          detail; the owner wants the customer to see what their gate is built
          from. What the customer copy still hides is the FABRICATION detail
          proper: the rail-cut dimension and the RAIL CUT PIECE callout inside
          the drawing, and the cantilever's end-upright cut callout. */}
      {(cuts.isCantilever ? (
        <>
          <text x={COL_TEXT_X} y={122} fill={INK} fontSize="11">Rails: {odLabel(cuts.railOdIn)}</text>
          <text x={COL_TEXT_X} y={138} fill={INK} fontSize="11">Uprights: {odLabel(cuts.uprightOdIn)}</text>
        </>
      ) : (
        <text x={COL_TEXT_X} y={130} fill={INK} fontSize="11">Frame: {frameLabel}</text>
      ))}
      <text x={COL_TEXT_X} y={154} fill={INK} fontSize="11">{m.frameFinish || ''} Frame</text>

      {/* FRAME ONLY takes the two baselines the mesh lines vacate (178, and 194
          for the sub-line), so the barbed wire at 226 and ACTUAL SIZE at
          250/266 keep their places and nothing below has to move. A mesh finish
          and a gauge printed on a gate that gets no mesh would send the shop
          looking for material that was never ordered, so both lines go. The
          FRAME finish stays — the frame is still being finished. */}
      {frameOnly ? (
        <>
          <text x={COL_TEXT_X} y={178} fill={INK} fontSize="11" fontWeight="700">
            FRAME ONLY
          </text>
          <text x={COL_TEXT_X} y={194} fill={INK} fontSize="9">
            No mesh
          </text>
          {/* Nailers can only exist under FRAME ONLY, so this line lives inside
              that branch and takes 210 — the last baseline the suppressed gauge
              line freed up. Barbed wire at 226 is untouched. */}
          {cuts.nailers2x4 && (
            <text x={COL_TEXT_X} y={210} fill={INK} fontSize="11">
              With 2x4 nailers
            </text>
          )}
        </>
      ) : (
        <>
          <text x={COL_TEXT_X} y={178} fill={INK} fontSize="11">{m.meshFinish || ''} Mesh</text>
          <text x={COL_TEXT_X} y={202} fill={INK} fontSize="11">{m.meshGauge || ''} gauge</text>
        </>
      )}

      {/* Barbed wire — omitted entirely when the gate has none. */}
      {cuts.barbedWire && (
        <text x={COL_TEXT_X} y={226} fill={INK} fontSize="11">
          {cuts.barbStrandCount} Strand Barbed Wire
        </text>
      )}

      {/* ACTUAL SIZE — no formula was applied to these numbers. Called out so a
          welder seeing an unusual cut knows it was deliberate, not a slip. */}
      {cuts.actualSize && (
        <>
          <text x={COL_TEXT_X} y={250} fill={INK} fontSize="11" fontWeight="700">
            ACTUAL SIZE
          </text>
          <text x={COL_TEXT_X} y={266} fill={INK} fontSize="9">
            No deductions applied
          </text>
          {/* THE WELDER'S RAIL LENGTH, beside the mode that produced it.
              The two numbers at the top of this column are what the customer
              approved (the outside width) and the upright cut; this is the pipe
              the rail is actually cut from, after the uprights come out and the
              notch allowance goes back in.

              282/296 are free on this sheet: the cantilever lines that use 268+
              cannot coexist with actual size, and FULL HEIGHT at 250/266 is
              suppressed under it. Shop copy only — showRailCutPiece is false on
              a customer copy — and the whole column is already dropped on a
              blank sheet. */}
          {cuts.showRailCutPiece && (
            <>
              <text x={COL_TEXT_X} y={282} fill={INK} fontSize="11" fontWeight="700">
                Rail cut piece: {cuts.railCutPieceLabel}
              </text>
              {/* Said only for a DOUBLE, where the entered width is per leaf
                  and so is this cut. No quantity is claimed: how many rails a
                  leaf takes is the drawing's business (two, plus a mid rail on
                  a tall gate), not this label's. */}
              {cuts.leafCount === 2 && (
                <text x={COL_TEXT_X} y={296} fill={INK} fontSize="9">
                  Per leaf
                </text>
              )}
            </>
          )}
        </>
      )}

      {/* FULL HEIGHT — the short-gate deduction was deliberately skipped, so a
             48" gate really is 48" tall. Split across two lines the way FRAME
             ONLY and ACTUAL SIZE are, because the sentence does not fit the
             column on one line at this size.

             Suppressed under actual size: no deduction would have applied there
             either, and the ACTUAL SIZE lines already say as much. That makes
             the two mutually exclusive, so this safely reuses 250/266. */}
      {cuts.fullHeight && !cuts.actualSize && (
        <>
          <text x={COL_TEXT_X} y={250} fill={INK} fontSize="11">
            Full height
          </text>
          <text x={COL_TEXT_X} y={266} fill={INK} fontSize="9">
            No 1.5&quot; deduction
          </text>
        </>
      )}

      {/* Which face the wire is on, in words as well as in the drawing. */}
      {cuts.isCantilever && cuts.barbedWire && (
        <text x={COL_TEXT_X} y={244} fill={INK} fontSize="11">
          {cuts.hangSide === 'Inside' ? 'Far face' : 'Near face'}
        </text>
      )}

      {/* Cantilever-only specification. */}
      {cuts.isCantilever && (
        <>
          <text x={COL_TEXT_X} y={268} fill={INK} fontSize="11">Slide: {cuts.slideDirection}</text>
          <text x={COL_TEXT_X} y={292} fill={INK} fontSize="11">Hung: {cuts.hangSide}</text>
          <text x={COL_TEXT_X} y={316} fill={INK} fontSize="11">{cuts.operation}</text>
        </>
      )}

      {/* ── MEMBER COUNT OVERRIDDEN ──
          Short, and only when it happened: the shop has to know an unusual
          number of rails or verticals was ASKED FOR rather than mis-drawn.
          Each line carries the count AND the length to cut each piece to, so
          the sheet stays truthful about what the welder is cutting — an extra
          rail is another piece of the same rail cut, not a new size.

          Shop copy only: it is fabrication detail, like the cut callout. The
          baselines are free on a swing sheet (the cantilever lines above cannot
          coexist — an override is swing-only). */}
      {cuts.membersOverridden && !customerCopy && (
        <>
          <text x={COL_TEXT_X} y={310} fill={INK} fontSize="11" fontWeight="700">
            MEMBERS OVERRIDDEN
          </text>
          {cuts.horizontalOverridden && (
            <text x={COL_TEXT_X} y={324} fill={INK} fontSize="9">
              {cuts.horizontalRailCount} rails @ {cuts.railCutLabel} (rule {cuts.horizontalRuleCount})
            </text>
          )}
          {cuts.verticalOverridden && (
            <text x={COL_TEXT_X} y={cuts.horizontalOverridden ? 338 : 324} fill={INK} fontSize="9">
              {cuts.verticalMidRailCount} vertical{cuts.verticalMidRailCount === 1 ? '' : 's'}
              {cuts.verticalMidRailCount > 0 ? ` @ ${cuts.verticalCutLabel}` : ''} (rule {cuts.verticalRuleCount})
            </text>
          )}
        </>
      )}

      {/* Bracing warning. This gate is wide enough to require diagonals, but the
          diagonal members are still an unimplemented placeholder in
          GateElevation — so the sheet is genuinely missing a structural member.
          Printed onto the sheet itself so the warning survives even if the
          drawing leaves the app by some route that bypasses the editor. */}
      </>
      )}

      {/* Which way the sheet is drawn. A slide gate is handed, so the shop has
          to know the viewpoint; a swing leaf is symmetrical and gets no note. */}
      {cuts.isCantilever && (
        <text
          x={SHEET_W / 2}
          y={505}
          fill={INK}
          fontSize="10"
          textAnchor="middle"
          letterSpacing="1.5"
        >
          VIEWED FROM OUTSIDE LOOKING IN
        </text>
      )}

      {/* Quantity — top-left of the drawing, on every version of the sheet.
          Outside the blank-sheet guard on purpose: a hand-filled template still
          has to say how many are being built. Nothing is drawn at a quantity
          of 1. */}
      {showQty && (
        <text x={finalX} y={QTY_Y} fill={INK} fontSize="20" fontWeight="700" letterSpacing="1">
          QTY: {quantity}
        </text>
      )}

      {/* Overall height — top-RIGHT of the drawing, mirroring QTY opposite it.
          Cantilever with barbed wire only; silent otherwise, the same way QTY
          is silent at a quantity of 1. Outside the blank-sheet guard for the
          same reason QTY is: a hand-filled template still has to say how tall
          the finished gate ends up. */}
      {showHeightSum && (
        <text
          x={finalX + finalW}
          y={heightY}
          fill={INK}
          fontSize="20"
          fontWeight="700"
          letterSpacing="1"
          textAnchor="end"
        >
          {heightSumText}
        </text>
      )}

      {/* ── Customer approval copy: heading and signature block ──
          Outside the blank-sheet guard on purpose: a blank template can also be
          a customer copy, and it still needs the heading and the signatures.
          Both are centred on the DRAWING, which never crosses the divider. */}
      {customerCopy && (
        <>
          <text
            x={finalX + finalW / 2}
            y={HEADING_Y}
            fill={INK}
            fontSize="12"
            fontWeight="700"
            textAnchor="middle"
            letterSpacing="2"
          >
            {HEADING_TEXT}
          </text>

          {/* Second heading line — swing gates not built to actual size. A
              swing gate fits INSIDE its opening, so the finished gate is
              narrower than the figure the customer gave, and that is the number
              they remember. Saying so up here, under the title, puts it in
              front of them before they look at the drawing. */}
          {showOpeningWarning && (
            <text
              x={finalX + finalW / 2}
              y={WARNING_Y}
              fill={INK}
              fontSize="11"
              fontWeight="700"
              textAnchor="middle"
              letterSpacing="0.5"
            >
              {WARNING_TEXT}
            </text>
          )}

          <line x1={finalX} y1={SIG_RULE_Y} x2={finalX + sigNameW} y2={SIG_RULE_Y} stroke={INK} strokeWidth={W_RULE} />
          <text x={finalX} y={SIG_LABEL_Y} fill={INK} fontSize="9">Customer Signature</text>

          <line x1={sigDateX} y1={SIG_RULE_Y} x2={sigDateX + sigDateW} y2={SIG_RULE_Y} stroke={INK} strokeWidth={W_RULE} />
          <text x={sigDateX} y={SIG_LABEL_Y} fill={INK} fontSize="9">Date</text>
        </>
      )}

      {/* ── Bottom strip — identical on a blank sheet ── */}

      {/* Left cell — company, timestamp, and who drew it. The name is
          uppercased to match how the app prints the salesperson on quotes and
          invoices. The line is omitted entirely when drawnBy is missing. */}
      <text x={24} y={STRIP_LINE_1} fill={INK} fontSize="11">Atlantic Fence &amp; Supply</text>
      <text x={24} y={STRIP_LINE_2} fill={INK} fontSize="11">{m.dateText || ''}</text>
      <text x={24} y={STRIP_LINE_3} fill={INK} fontSize="11">{m.timeText || ''}</text>
      {m.drawnBy && (
        <text x={24} y={STRIP_LINE_4} fill={INK} fontSize="11">
          Drawn by: {String(m.drawnBy).toUpperCase()}
        </text>
      )}

      {/* Middle cell — customer, on the same baseline as the company name,
          with the related document beneath it. documentRef is printed exactly
          as given ("Quote #…" / "Invoice #…"); this does not parse the type. */}
      <text x={394} y={STRIP_LINE_1} fill={INK} fontSize="11">{m.customerName || ''}</text>
      {m.documentRef && (
        <text x={394} y={STRIP_LINE_2} fill={INK} fontSize="11">{m.documentRef}</text>
      )}

      {/* Right cell — scale note, centred in the strip */}
      <text x={COL_TEXT_X} y={565} fill={INK} fontSize="11">Not to Scale</text>
    </svg>
  );
}
